import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './HScroller.scss';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

/**
 * Single-row horizontal scroller with previous / next arrows.
 *
 * Each arrow only shows while there is more content in its direction, so a
 * row that fits never displays controls. Content narrower than the track is
 * centred (`align="center"`) or left-aligned (`align="start"`); content wider
 * than the track always starts at the left edge so nothing is unreachable.
 *
 * `variant` picks the size of the controls: `bar` for the slim category bar,
 * `tiles` for image rails.
 */
const HScroller = ({
  label,
  variant = 'tiles',
  align = 'center',
  className = '',
  children,
}) => {
  const trackRef = useRef(null);
  const contentRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    setCanPrev(track.scrollLeft > 2);
    setCanNext(track.scrollLeft < max - 2);
  }, []);

  // Re-measure on scroll and whenever either the track or its content
  // changes size (window resize, categories arriving after the skeleton).
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    update();
    track.addEventListener('scroll', update, { passive: true });

    let observer;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(update);
      observer.observe(track);
      if (contentRef.current) observer.observe(contentRef.current);
    } else {
      window.addEventListener('resize', update);
    }

    return () => {
      track.removeEventListener('scroll', update);
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, [update]);

  const scroll = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({
      left: direction * track.clientWidth * 0.8,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  const classes = [
    'ij-hscroll',
    `ij-hscroll--${variant}`,
    `ij-hscroll--${align}`,
    canPrev && 'can-prev',
    canNext && 'can-next',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <button
        type="button"
        className="ij-hscroll__arrow ij-hscroll__arrow--prev"
        aria-label={`Scroll ${label} left`}
        onClick={() => scroll(-1)}
        disabled={!canPrev}
      >
        <FiChevronLeft aria-hidden="true" />
      </button>

      <div className="ij-hscroll__track" ref={trackRef}>
        <div className="ij-hscroll__content" ref={contentRef}>
          {children}
        </div>
      </div>

      <button
        type="button"
        className="ij-hscroll__arrow ij-hscroll__arrow--next"
        aria-label={`Scroll ${label} right`}
        onClick={() => scroll(1)}
        disabled={!canNext}
      >
        <FiChevronRight aria-hidden="true" />
      </button>
    </div>
  );
};

export default HScroller;
